import React, { useContext } from 'react';
import {Datapoint} from '../../dashboard/team-results-store';
import {StoreContext} from '../root-store';
import {Grid, GridColumn, Progress} from '../../../../patterns';
import {formatToDayMonthAndYear} from '../dateUtils';
import {Period} from '../../user/user-store';
import {DistributionBars} from '../distributionbars/DistributionBars';

export interface IScoreTooltipProps {
    period: Period;
    name?: string;
    categoryName?: string;
    compareToName?: string;
    value: Datapoint;
    compareTo?: Datapoint;
    showAnswerDistribution?: boolean;
}

export const ScoreTooltip = ({
    showAnswerDistribution = true,
    ...props
}: IScoreTooltipProps): JSX.Element => {
    const store = useContext(StoreContext).labelStore;
    const compareToScore = props.compareTo ? props.compareTo.scorePercentage : 50;

    const score = props.value.scorePercentage;
    const gridColumnWidth = props.compareToName ? 'xs-6' : 'xs-12';

    const isCompareToTarget =
        props.compareTo &&
        props.compareTo.prepared === 0 &&
        props.compareTo.answered === 0 &&
        (props.compareTo.scorePercentage > 0 || props.compareTo.participationPercentage > 0);

    const shouldShowCompareTo = props.compareTo && !isCompareToTarget;

    return (
        <div className="text score-tooltip">
            <div className="score-tooltip__date">
                {formatToDayMonthAndYear(props.period.from)} - {formatToDayMonthAndYear(props.period.to)}
            </div>

            <Grid gutter="large" verticalGutter="large">
                {props.name && (
                    <GridColumn width={[gridColumnWidth]}>
                        <div className="score-tooltip__section">{props.name}</div>
                    </GridColumn>
                )}
                {props.categoryName && (
                    <GridColumn width={[gridColumnWidth]}>
                        <div className="score-tooltip__section">{props.categoryName}</div>
                    </GridColumn>
                )}
                {props.compareToName && (
                    <GridColumn width={[gridColumnWidth]}>
                        <div className="score-tooltip__section">{props.compareToName}</div>
                    </GridColumn>
                )}
            </Grid>


            <Grid gutter="small" verticalGutter="none">
                <GridColumn width={[gridColumnWidth]}>
                    <Progress
                        icon="like"
                        color={score >= compareToScore ? 'green' : 'red'}
                        label={store.label('dashboard.tooltip.score', { score_percentage: score.toString() })}
                        percent={score}
                    />
                </GridColumn>

                {props.compareTo && (
                    <GridColumn width={[gridColumnWidth]}>
                        <Progress
                            icon="like"
                            color={compareToScore >= score ? 'green' : 'red'}
                            label={store.label('dashboard.tooltip.score', { score_percentage: compareToScore.toString() })}
                            percent={compareToScore}
                        />
                    </GridColumn>
                )}
            </Grid>

            <Grid gutter="small" verticalGutter="none">
                <GridColumn width={[gridColumnWidth]}>
                    <Progress
                        icon="users"
                        color="blue"
                        label={store.label('dashboard.tooltip.activity-percentage', {
                            answers_percentage: props.value.participationPercentage.toString(),
                        })}
                        percent={props.value.participationPercentage}
                    />
                </GridColumn>

                {props.compareTo && (
                    <GridColumn width={[gridColumnWidth]}>
                        <Progress
                            icon="users"
                            color="blue"
                            label={store.label('dashboard.tooltip.activity-percentage', {
                                answers_percentage: props.compareTo.participationPercentage.toString(),
                            })}
                            percent={props.compareTo.participationPercentage}
                        />
                    </GridColumn>
                )}
            </Grid>
            {showAnswerDistribution && (
                <div className="score-tooltip__charts">
                    <div className="score-tooltip__charts-header">
                        {store.label('dashboard.tooltip.answer-distribution')}
                    </div>
                    <Grid gutter="small" verticalGutter="none">
                        <GridColumn width={['xs-6']}>
                            <DistributionBars data={props.value} show={showAnswerDistribution} />
                        </GridColumn>
                        <GridColumn width={['xs-6']}>
                            <DistributionBars
                                data={props.compareTo}
                                hidden={!shouldShowCompareTo}
                                show={showAnswerDistribution}
                            />
                        </GridColumn>
                    </Grid>
                </div>
            )}

        </div>
    );
};
